import React, {useMemo} from "react";
import {Autocomplete, Box, Chip, TextField, Typography, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {ICSAncillary} from "../../../models/models";

/** Opzione del controllo multi-scelta: mappa 1:1 su una ancillary sottostante. */
export interface IChangeGroupOption<T = ICSAncillary> {
	/** `ancillaryCode` (es. CG5): chiave stabile della selezione */
	code: string;
	/** etichetta visualizzata (nome ancillary nella lingua corrente) */
	label: string;
	/** entità sottostante, immutata dal componente */
	ancillary: T;
}

export interface IChangeGroupMultiSelectProps<T = ICSAncillary> {
	/** opzioni = ancillary Change Group (`^CG\d+$`) della vista */
	options: IChangeGroupOption<T>[];
	/** codici attualmente selezionati */
	selectedCodes: string[];
	/** callback: nuovo insieme di codici selezionati (mappati sulle stesse ancillary) */
	onChange: (selectedCodes: string[]) => void;
	/** etichetta del controllo (fallback su chiave i18n) */
	label?: string;
	disabled?: boolean;
	/** render opzionale del prezzo per opzione (per non alterare la logica di calcolo) */
	renderPrice?: (ancillary: T) => React.ReactNode;
	/** identificativo per test/aria */
	testId?: string;
}

/**
 * LPP-22293 / US-NTWRA-005 (RDT §3 SCFE-1/SCFE-2, §2.1) — Change Group come singola voce a
 * scelta multipla (MUI Autocomplete multiple) in luogo di N box separati. Riusato da
 * create-ra (`ICSAncillary`) e dalle viste ra-detail/ra-edit (`IRADAncillary`): il componente
 * è generico sul tipo ancillary, restando un unico punto di verità presentazionale.
 *
 * Componente presentazionale: non conosce form/recoil. La selezione/deselezione emette i
 * `code` selezionati; il wiring verso il form e verso `counterSaleManagement` (units 1/0
 * sulle stesse ancillary) è responsabilità del contenitore, così da mantenere payload,
 * prezzo e totale invariati (Requirement 2, RF-07).
 */
const ChangeGroupMultiSelect = <T = ICSAncillary,>({
	options,
	selectedCodes,
	onChange,
	label,
	disabled,
	renderPrice,
	testId,
}: IChangeGroupMultiSelectProps<T>) => {
	const theme = useTheme();
	const {t} = useTranslation(["network"]);

	const selectedOptions = useMemo(
		() => options.filter((opt) => selectedCodes.includes(opt.code)),
		[options, selectedCodes]
	);

	const controlLabel = label ?? t("raFilling.ancillariesInfo.changeGroup.label");

	return (
		<Box
			sx={{width: "100%", py: theme.spacing(1)}}
			data-testid={testId ? `change-group-multiselect-${testId}` : "change-group-multiselect"}
		>
			<Autocomplete<IChangeGroupOption<T>, true>
				multiple
				disableCloseOnSelect
				disabled={disabled}
				options={options}
				value={selectedOptions}
				getOptionLabel={(option) => option.label}
				isOptionEqualToValue={(option, value) => option.code === value.code}
				onChange={(_event, value) => onChange(value.map((opt) => opt.code))}
				renderOption={(props, option) => (
					<li {...props} key={option.code} data-testid={`change-group-option-${option.code}`}>
						<Box sx={{display: "flex", justifyContent: "space-between", width: "100%"}}>
							<Typography variant={"body"} component={"span"}>
								{option.label}
							</Typography>
							{renderPrice && (
								<Typography variant={"body"} component={"span"} sx={{ml: theme.spacing(2)}}>
									{renderPrice(option.ancillary)}
								</Typography>
							)}
						</Box>
					</li>
				)}
				renderTags={(value, getTagProps) =>
					value.map((option, index) => (
						<Chip
							{...getTagProps({index})}
							key={option.code}
							label={option.label}
							data-testid={`change-group-chip-${option.code}`}
						/>
					))
				}
				renderInput={(params) => (
					<TextField
						{...params}
						label={controlLabel}
						placeholder={t("raFilling.ancillariesInfo.changeGroup.placeholder")}
						inputProps={{
							...params.inputProps,
							"data-testid": testId ? `change-group-input-${testId}` : "change-group-input",
						}}
					/>
				)}
			/>
		</Box>
	);
};

export default ChangeGroupMultiSelect;
